/*
 * farmLocation — where to send somebody to visit a farm, and how sure we are.
 *
 * ⚠ WHY A SECOND SOURCE EXISTS AT ALL. The visit route can only place a farm carrying
 * `farmers.lat/lon`, which is a geocoded MAILING ADDRESS. Measured on the live book
 * 2026-10-03: 282 farmers, 207 located, 27 with an address and no coordinate, 48 with
 * neither — so 75 of 282 (27%) print as "could not be placed" on every schedule. And the
 * address half cannot be fixed by geocoding harder: the US Census geocoder, which is the
 * source 175 of the 207 located rows already came from, resolves only 2 of those 27, because
 * TIGER carries no address ranges for rural WNY roads. Probed directly — Howlett's
 * "1112 E RIVER RD, AVON, NY 14414" returns ZERO matches on every benchmark and both
 * endpoint shapes while an urban control resolves fine. Ten of the 27 are PO boxes, which a
 * geocoder will happily place at a post office.
 *
 * But a farm with no usable address still has insured GROUND whose coordinates we hold:
 * `policy_fields.latitude/longitude` (RMA's own P27) and `jd_fields.centroid_lat/lon`.
 * That places 9 of the 75, and the count badly understates it — they are HOWLETT (90
 * fields), MEISENZAHL (230), PANEK (538), CY FARMS (212), WOODLAWN (75), RAUBERDALE (27),
 * CARLEY (9), PALLADINO (12) and SCHOFIELD (24). Some of the largest operations in the
 * book, every one unroutable today.
 *
 * ⚠ THE MEDIAN, NEVER THE MEAN — AND AUSTIC IS WHY. AUSTIC FARM PARTNERS has 518 fields
 * whose median point is 42.58, -76.74 in New York and SIX fields at 40.11, -92.54, which is
 * Missouri, 820 miles out. Those are not bad coordinates: one of them is NAMED "Millard MO".
 * He genuinely farms there, and the field's own name is the source that settles it — my first
 * read was "probably a bad coordinate" and it was wrong. A mean would drag his route target
 * hundreds of miles toward ground he visits once a year; a median ignores six of 518. The
 * component medians are taken independently, which is not the geometric median and does not
 * claim to be — it is a robust central point, which is all a day's grouping needs.
 *
 * ⚠ AND IT IS NEVER WRITTEN TO `farmers.lat/lon`. That column means "the address we
 * geocoded". A field centroid is a different derivation at a different accuracy, and mixing
 * two meanings into one column is the generation-integrity failure CLAUDE.md records — the
 * map and the route both colour from one scale. It is resolved at PLAN TIME and LABELLED.
 *
 * How good is a centroid as a route target? Measured over the 47 farms carrying BOTH a
 * geocoded address and fields: median 1.5 mi apart, 34 of 47 within 5 mi, 41 within 15,
 * p90 25.3 mi, worst 85.8 mi. Good enough to GROUP a day; not good enough to print as the
 * farmstead, which is why every surface says which source placed the stop.
 */
"use strict";

// The five states this book writes in, with slack. A median point outside them is a bad
// match however many fields agreed on it — the same footprint guard the geocoder uses.
const FOOTPRINT = Object.freeze({ minLat: 36.5, maxLat: 45.5, minLon: -85.5, maxLon: -71.5 });

const inFootprint = (lat, lon) =>
  Number.isFinite(lat) && Number.isFinite(lon) &&
  lat >= FOOTPRINT.minLat && lat <= FOOTPRINT.maxLat &&
  lon >= FOOTPRINT.minLon && lon <= FOOTPRINT.maxLon;

/**
 * A usable coordinate pair, or null.
 *
 * ⚠ `Number(null)` IS 0 AND 0 IS FINITE, so the null test comes FIRST. A (0, 0) pair is
 * refused outright: it is the Gulf of Guinea, and it is what an unpopulated column looks
 * like once something has coerced it. This repo has paid for that coercion more than once.
 */
function point(lat, lon) {
  if (lat == null || lon == null || lat === "" || lon === "") return null;
  const a = Number(lat), b = Number(lon);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  return { lat: a, lon: b };
}

/** The middle value, taking the LOWER of the two middles on an even count so the answer is
 *  one of the observations rather than a point between two of them. */
function median(nums) {
  if (!nums || !nums.length) return null;
  const s = [...nums].sort((x, y) => x - y);
  return s[Math.floor((s.length - 1) / 2)];
}

/**
 * The robust central point of a field cloud, component-wise. Returns null for an empty set
 * or a result outside the footprint.
 */
function medianPoint(points) {
  const ok = (points || []).map((p) => point(p.lat, p.lon)).filter(Boolean);
  if (!ok.length) return null;
  const lat = median(ok.map((p) => p.lat));
  const lon = median(ok.map((p) => p.lon));
  if (!inFootprint(lat, lon)) return null;
  return { lat, lon, n: ok.length };
}

/** Straight-line miles, for reporting how far a centroid sits from an address. */
function milesApart(a, b) {
  if (!a || !b) return null;
  const dy = (a.lat - b.lat) * 69;
  const dx = (a.lon - b.lon) * 69 * Math.cos(((a.lat + b.lat) / 2) * Math.PI / 180);
  return Math.sqrt(dy * dy + dx * dx);
}

const SOURCES = Object.freeze({
  address: "address",             // farmers.lat/lon — a geocoded mailing address
  rmaFields: "rma_fields",        // median of policy_fields.latitude/longitude (P27)
  jdFields: "jd_fields",          // median of jd_fields.centroid_lat/lon
  none: "none",
});

const SOURCE_LABEL = Object.freeze({
  [SOURCES.address]: "mailing address",
  [SOURCES.rmaFields]: "centre of their insured fields",
  [SOURCES.jdFields]: "centre of their machine-data fields",
  [SOURCES.none]: "no location on file",
});

/** True where the stop was placed by a field cloud rather than by an address. */
const isCentroid = (src) => src === SOURCES.rmaFields || src === SOURCES.jdFields;

/**
 * Resolve one farm's visit location.
 *
 * ⚠ THE ADDRESS ALWAYS WINS WHERE IT EXISTS, and that is not a preference — it is the only
 * one of the three that is the place you actually meet somebody. The fallback order after it
 * is RMA fields before John Deere fields, because `policy_fields` is the AIP's own P27 record
 * of ground this farm insures, while `jd_fields` is whatever machine data happens to have
 * been shared with us and includes custom work on ground the farm does not own.
 *
 * Returns { lat, lon, source, n, label, fallback } — or source 'none' with a null point,
 * never a guess. `fallback` is true where a centroid stood in for a missing address, which
 * is what every surface keys its caveat off.
 */
function resolveFarmLocation(farm, fields) {
  const f = farm || {};
  const direct = point(f.lat, f.lon);
  if (direct && inFootprint(direct.lat, direct.lon)) {
    return { ...direct, source: SOURCES.address, n: 1,
             label: SOURCE_LABEL[SOURCES.address], fallback: false };
  }
  const bag = fields || {};
  for (const [key, src] of [["rma", SOURCES.rmaFields], ["jd", SOURCES.jdFields]]) {
    const m = medianPoint(bag[key]);
    if (m) {
      return { lat: m.lat, lon: m.lon, source: src, n: m.n,
               label: SOURCE_LABEL[src], fallback: true };
    }
  }
  return { lat: null, lon: null, source: SOURCES.none, n: 0,
           label: SOURCE_LABEL[SOURCES.none], fallback: false,
           why: f.address ? "an address we could not place on a map" : "no address on file" };
}

/**
 * Resolve a whole roster, and REPORT the mix rather than just returning it.
 *
 * `fieldsByFarmer` is { [farmerId]: { rma: [{lat,lon}], jd: [{lat,lon}] } }.
 *
 * The counts are the point: a schedule that quietly placed a quarter of the book by field
 * centroid, or quietly dropped it, reads as a complete plan either way.
 */
function resolveFarmLocations(farms, fieldsByFarmer) {
  const out = {}, counts = { address: 0, rma_fields: 0, jd_fields: 0, none: 0 };
  for (const f of farms || []) {
    if (!f || f.id == null) continue;
    const r = resolveFarmLocation(f, (fieldsByFarmer || {})[f.id]);
    out[f.id] = r;
    counts[r.source] = (counts[r.source] || 0) + 1;
  }
  const placed = counts.address + counts.rma_fields + counts.jd_fields;
  return {
    byFarmer: out,
    counts,
    placed,
    total: (farms || []).filter((f) => f && f.id != null).length,
    centroidPlaced: counts.rma_fields + counts.jd_fields,
  };
}

/** Group raw field rows into the { rma, jd } shape above, keyed by farmer. */
function groupFieldPoints({ policyFields, jdFields } = {}) {
  const out = {};
  const put = (key, id, lat, lon) => {
    if (id == null) return;
    const p = point(lat, lon);
    if (!p) return;
    (out[id] = out[id] || { rma: [], jd: [] })[key].push(p);
  };
  for (const r of policyFields || []) put("rma", r.farmer_id, r.latitude, r.longitude);
  for (const r of jdFields || []) put("jd", r.farmer_id, r.centroid_lat, r.centroid_lon);
  return out;
}

module.exports = {
  FOOTPRINT, SOURCES, SOURCE_LABEL,
  point, median, medianPoint, milesApart, inFootprint, isCentroid,
  resolveFarmLocation, resolveFarmLocations, groupFieldPoints,
};
